import { Easing, linear } from './cubic-bezier';

/**
 * Progress of an animation that starts `delaySec` into the scene and lasts `durationSec`: 0 before, 1 after —
 * like a CSS animation with `fill-mode: both`. A zero duration jumps at the delay.
 */
export function progressAt(timeSec: number, delaySec: number, durationSec: number): number {
  if (durationSec <= 0) return timeSec >= delaySec ? 1 : 0;
  return Math.min(1, Math.max(0, (timeSec - delaySec) / durationSec));
}

export interface Tween {
  delaySec: number;
  durationSec: number;
  from: number;
  to: number;
  ease?: Easing;
}

/** The value of one property animated from `from` to `to` (CSS `animation` with `fill-mode: both`). */
export function tween(timeSec: number, { delaySec, durationSec, from, to, ease = linear }: Tween): number {
  return from + (to - from) * ease(progressAt(timeSec, delaySec, durationSec));
}

export interface Keyframe {
  /** Seconds from the scene start. */
  atSec: number;
  value: number;
  /** Easing of the segment that starts at this keyframe, as `animation-timing-function` inside `@keyframes`. */
  ease?: Easing;
}

/**
 * The value at `timeSec` on a chain of keyframes, sorted by time: held at the first before it, at the last after it.
 * Each segment is eased by its starting keyframe, the way CSS applies a timing function per keyframe.
 */
export function keyframes(timeSec: number, frames: readonly Keyframe[]): number {
  if (frames.length === 0) return 0;
  if (timeSec <= frames[0].atSec) return frames[0].value;
  for (let i = 1; i < frames.length; i++) {
    const end = frames[i];
    if (timeSec < end.atSec) {
      const start = frames[i - 1];
      const k = (start.ease ?? linear)((timeSec - start.atSec) / (end.atSec - start.atSec));
      return start.value + (end.value - start.value) * k;
    }
  }
  return frames[frames.length - 1].value;
}
